import { RootActions, RootState } from "common/reducers";
import { receive } from "common/reducers/Utils";
import { List, Map, Record } from "immutable";
import {
  RECEIVE_ESTIMATE_GROUP,
  UPDATE_ESTIMATE_GROUP_TOTALS,
  DECREMENT_ESTIMATE_GROUP_UNSAVED_ID,
  SET_ESTIMATE_GROUP_BY_ID,
  DUPLICATE_ESTIMATE_GROUP,
  COPY_ESTIMATE_GROUP_TO_LAST_SAVED,
  START_RENAMING_GROUP,
  UPDATE_RENAMING_GROUP_DRAFT_NAME,
  STOP_RENAMING_GROUP,
  EstimateGroupActions,
} from "common/reducers/estimateGroup.actions";
import { EstimateLineItemActions } from "common/reducers/estimateLineItem.actions";
import { estimateGroupFromJSON, EstimateGroupRecord } from "common/records/EstimateGroup";
import { estimateLineItemsReducer } from "common/reducers/estimateLineItem.reducer";
import { estimateGroup, unsavedId as estimateGroupUnsavedIdSelector } from "common/selectors/estimateGroup.selectors";
import { estimate } from "common/selectors/estimate.selectors";
import { EstimateRecord } from "common/records/Estimate";
import { filteredEstimateLineItems } from "common/selectors/estimateLineItem.selectors";
import { sumEstimateLineItemTotals } from "common/records/EstimateLineItem";

export const EstimateGroupStateRecord = Record({
  byId: Map<number, EstimateGroupRecord>(),
  lastSavedById: Map<number, EstimateGroupRecord>(),
  editingSnapshotById: Map<number, EstimateGroupRecord>(),
  unsavedId: -1,
  renamingGroupId: null as number | null,
  renamingGroupDraftName: "" as string,
});

export const initialState = EstimateGroupStateRecord();

export const model = "estimateGroups";

export const estimateGroupsReducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    return state.set(model, initialState);
  }

  switch (action.type) {
    case RECEIVE_ESTIMATE_GROUP: {
      const estimateGroup = action.payload.estimateGroup;

      estimateGroup?.line_items?.map((li) => {
        state = estimateLineItemsReducer(state, EstimateLineItemActions.receiveEstimateLineItem(li));
      });

      const groupRecord = estimateGroupFromJSON(estimateGroup);
      state = receive(state, model, groupRecord);

      // Copy to lastSavedById - represents what's saved on the SERVER
      state = state.setIn([model, "lastSavedById", estimateGroup.id], groupRecord);

      return state;
    }

    case UPDATE_ESTIMATE_GROUP_TOTALS: {
      const { estimateGroupId } = action.payload;
      const estimateGroupRecord = estimateGroup(state, { estimateGroupId });
      const estimateRecord = estimate(state, { estimateId: estimateGroupRecord?.estimate_id });

      if (!estimateGroupRecord || !estimateRecord) {
        console.error("Missing required data in redux to calculate estimate group totals", {
          estimateGroupId,
          estimateGroup: estimateGroupRecord?.toJS(),
          estimate: estimateRecord?.toJS(),
        });
        return state;
      }

      const lineItems = filteredEstimateLineItems(state, { estimateLineItemIds: estimateGroupRecord.line_item_ids });

      const lineItemTotals = sumEstimateLineItemTotals(lineItems);
      const newEstimateGroupRecord = estimateGroupRecord.merge(lineItemTotals);
      return state.setIn([model, "byId", estimateGroupId], newEstimateGroupRecord);
    }

    case DECREMENT_ESTIMATE_GROUP_UNSAVED_ID: {
      const currentUnsavedId = state.getIn([model, "unsavedId"]) as number;
      return state.setIn([model, "unsavedId"], currentUnsavedId - 1);
    }

    case SET_ESTIMATE_GROUP_BY_ID: {
      const { estimateGroupId, estimateGroup: estimateGroupData } = action.payload;
      const estimateGroupRecord = estimateGroupFromJSON(estimateGroupData);
      return state.setIn([model, "byId", estimateGroupId], estimateGroupRecord);
    }

    case DUPLICATE_ESTIMATE_GROUP: {
      const { sourceGroupId, targetEstimateId } = action.payload;

      const originalGroup = estimateGroup(state, { estimateGroupId: sourceGroupId });
      if (!originalGroup || originalGroup._destroy) {
        return state;
      }

      const newGroupId = estimateGroupUnsavedIdSelector(state);
      state = estimateGroupsReducer(state, EstimateGroupActions.decrementUnsavedId());

      const duplicatedGroupData = {
        ...originalGroup.toJS(),
        id: newGroupId,
        estimate_id: targetEstimateId,
        line_item_ids: [],
      };
      state = estimateGroupsReducer(
        state,
        EstimateGroupActions.setEstimateGroupById(newGroupId, duplicatedGroupData as any),
      );
      state = state.setIn([model, "byId", newGroupId, "line_item_ids"], List<number>());

      const targetEstimateRecord = estimate(state, { estimateId: targetEstimateId }) as EstimateRecord;
      if (targetEstimateRecord) {
        const updatedGroupIds = targetEstimateRecord.group_ids.push(newGroupId);
        state = state.setIn(["estimates", "byId", targetEstimateId, "group_ids"], updatedGroupIds);
      }

      originalGroup.line_item_ids.forEach((lineItemId: number) => {
        state = estimateLineItemsReducer(
          state,
          EstimateLineItemActions.duplicateEstimateLineItem(lineItemId, newGroupId),
        );
      });

      return state;
    }

    case COPY_ESTIMATE_GROUP_TO_LAST_SAVED: {
      const { estimateGroupId } = action.payload;
      const groupRecord = state.getIn([model, "byId", estimateGroupId]);
      if (!groupRecord) {
        return state;
      }
      return state.setIn([model, "lastSavedById", estimateGroupId], groupRecord);
    }

    case START_RENAMING_GROUP: {
      const { groupId, draftName } = action.payload;
      return state.setIn([model, "renamingGroupId"], groupId).setIn([model, "renamingGroupDraftName"], draftName);
    }

    case UPDATE_RENAMING_GROUP_DRAFT_NAME: {
      const { draftName } = action.payload;
      return state.setIn([model, "renamingGroupDraftName"], draftName);
    }

    case STOP_RENAMING_GROUP: {
      return state.setIn([model, "renamingGroupId"], null).setIn([model, "renamingGroupDraftName"], "");
    }

    default:
      return state;
  }
};
